import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Image,
    Flex,
    Button,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import RTE from '@consteed/frontend/components/pageDesigner/RTE'
import {motion} from 'framer-motion'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils'
import {convertSfccHrefToPwaHref} from '@consteed/frontend/utils/richTextConfigurations'
import Link from '@consteed/overrides/components/link'
import {Link as ChakraLink} from '@salesforce/retail-react-app/app/components/shared/ui/Link'
import {NavigateNextIcon} from '@consteed/overrides/components/icons'
import {HideOnDesktop, HideOnMobile} from '@consteed/overrides/components/responsive'
import ProductFeatures from '@consteed/frontend/components/productFeatures'
import Benefits from '@consteed/frontend/components/benefits'

// @todo: add responsive behaviour/image-sizes
const Picture = ({image, alt}) => (
    <picture>
        <source srcSet={image?.src?.tablet} media="(min-width: 48em)" />
        <source srcSet={image?.src?.desktop} media="(min-width: 64em)" />
        <Image
            src={image?.src?.mobile ? image?.src?.mobile : image}
            ignoreFallback={true}
            alt={alt}
            title={alt}
        />
    </picture>
)
Picture.propTypes = {
    image: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            _type: PropTypes.string,
            focalPoint: PropTypes.shape({
                _type: PropTypes.string,
                x: PropTypes.number,
                y: PropTypes.number
            }),
            metaData: PropTypes.shape({
                _type: PropTypes.string,
                height: PropTypes.number,
                width: PropTypes.number
            }),
            url: PropTypes.string,
            src: PropTypes.string || PropTypes.object
        })
    ]),
    alt: PropTypes.string
}

const MotionBox = motion(Box)
const animations = {
    hiddenContainer: {
        y: '20%'
    },
    slideInContainer: {
        y: '0',
        transition: {
            ease: 'easeInOut',
            duration: 0.5
        }
    },
    hiddenImage: {
        y: '30%'
    },
    slideInImage: {
        y: '-5%',
        transition: {
            ease: 'easeInOut',
            duration: 1
        }
    }
}

const ComponentVariant = ({headline, buttonText, buttonLink, variant}) => {
    const pwaButtonLink = convertSfccHrefToPwaHref(buttonLink)
    const isAbsolute = isAbsoluteURL(pwaButtonLink)
    const LinkWrapper = isAbsolute ? ChakraLink : Link
    const linkProps = isAbsolute ? {href: pwaButtonLink} : {to: pwaButtonLink}
    const styles = useMultiStyleConfig('LandingPageDiscover', {variant})
    const imageOnTheRight = variant === 'textLeftImageRight'

    return (
        <Box className={'pdcOuterBox'}>
            <MotionBox
                variants={animations}
                initial="hiddenContainer"
                whileInView="slideInContainer"
            >
                <Box>
                    <Flex
                        direction={{base: 'column', md: 'row'}}
                        align="center"
                        gap={4}
                        {...styles.container}
                    >
                        <Box {...styles.orangeCircle}></Box>
                        {!imageOnTheRight && (
                            <HideOnMobile>
                                <MotionBox
                                    variants={animations}
                                    initial="hiddenImage"
                                    whileInView="slideInImage"
                                    {...styles.horseShoe}
                                >
                                    <Picture
                                        image={getAssetUrl('static/img/products/horseShoe.png')}
                                    />
                                </MotionBox>
                            </HideOnMobile>
                        )}
                        <Box p={{base: 8, xl: 0}}>
                            {headline && (
                                <RTE
                                    isNested={true}
                                    richText={headline}
                                    {...styles.headline}
                                    textStyles={{base: 'light3Xlarge', xl: 'light6Xlarge'}}
                                    marginLeft={0}
                                    marginRight={0}
                                    maxWidth={'610px'}
                                />
                            )}
                            <ProductFeatures />
                            <Flex>
                                <HideOnMobile>
                                    <Benefits />
                                </HideOnMobile>
                                {imageOnTheRight && (
                                    <LinkWrapper {...linkProps}>
                                        <Button
                                            variant={'primary'}
                                            size={'md'}
                                            rightIcon={<NavigateNextIcon />}
                                            width="inherit"
                                            mt={5}
                                        >
                                            {buttonText}
                                        </Button>
                                    </LinkWrapper>
                                )}
                            </Flex>

                            {!imageOnTheRight && (
                                <LinkWrapper {...linkProps}>
                                    <Button
                                        variant={'primary'}
                                        size={'md'}
                                        rightIcon={<NavigateNextIcon />}
                                        width="inherit"
                                        mt={5}
                                    >
                                        {buttonText}
                                    </Button>
                                </LinkWrapper>
                            )}
                        </Box>
                    </Flex>

                    {imageOnTheRight && (
                        <React.Fragment>
                            <MotionBox
                                variants={animations}
                                initial="hiddenImage"
                                whileInView="slideInImage"
                                {...styles.horseShoe}
                            >
                                <Picture image={getAssetUrl('static/img/products/horseShoe.png')} />
                            </MotionBox>
                            <MotionBox
                                variants={animations}
                                initial="hiddenImage"
                                whileInView="slideInImage"
                                {...styles.iphone}
                            >
                                <Picture
                                    image={getAssetUrl('static/img/landingPageDiscover/iphone.png')}
                                />
                            </MotionBox>
                            <MotionBox
                                variants={animations}
                                initial="hiddenImage"
                                whileInView="slideInImage"
                                {...styles.signet}
                            >
                                <Picture
                                    image={getAssetUrl('static/img/landingPageDiscover/signet.png')}
                                />
                            </MotionBox>
                        </React.Fragment>
                    )}
                    {!imageOnTheRight && (
                        <HideOnDesktop>
                            <MotionBox
                                variants={animations}
                                initial="hiddenImage"
                                whileInView="slideInImage"
                                {...styles.mobileProductImage}
                            >
                                <Picture image={getAssetUrl('static/img/products/horseShoe.png')} />
                            </MotionBox>
                        </HideOnDesktop>
                    )}
                </Box>
            </MotionBox>
        </Box>
    )
}
ComponentVariant.propTypes = {
    layout: PropTypes.string,
    headline: PropTypes.string,
    buttonText: PropTypes.string,
    buttonLink: PropTypes.string,
    variant: PropTypes.string
}

/**
 * LandingPageDiscover component
 *
 * @param {object} props
 * @param {string} - props.layout - Layout (Image-Left-Text-Right || Text-Left-Image-Right)
 * @param {string} - props.buttonText - Button Text.
 * @param {string} - props.buttonLink - Button Link.
 * @returns {React.ReactElement} - LandingPageDiscover component.
 */
const LandingPageDiscover = ({layout, headline, buttonText, buttonLink}) => {
    const variant = layout === 'Text-Left-Image-Right' ? 'textLeftImageRight' : 'imageLeftTextRight'

    return (
        <ComponentVariant
            headline={headline}
            buttonText={buttonText}
            buttonLink={buttonLink}
            variant={variant}
        />
    )
}

LandingPageDiscover.propTypes = {
    layout: PropTypes.string,
    headline: PropTypes.string,
    buttonText: PropTypes.string,
    buttonLink: PropTypes.string
}

export default LandingPageDiscover
